// Cycle 463.1 — what the delete-account confirmation says about billing.
//
// Operator decision: a web (Stripe) subscription is cancelled immediately by
// the deletion itself, with no automatic refund. A subscription billed through
// the App Store or Google Play cannot be cancelled for the person, so the
// confirmation tells them to cancel it there BEFORE they confirm.
//
// Pure: the Settings handler fetches `GET /api/account/deletion-preview` and
// hands the result (or null when it could not be read) to these functions.

export type DeletionPreview = {
  cancels_web_subscription: boolean;
  store_billing: string[];
};

export const DELETE_ACCOUNT_BODY =
  "This permanently deletes your Sugar Alarm account and ALL of its " +
  "data — every member you manage, their glucose history, " +
  "treatments, alerts, devices, and reminders. This cannot be undone.";

const STORE_NAMES: Record<string, string> = {
  apple: "the App Store",
  google: "Google Play",
};

/** The store-billing line, or null when no store still bills the account. */
export function storeBillingLine(stores: readonly string[]): string | null {
  const names = stores
    .map((s) => STORE_NAMES[s])
    .filter((n): n is string => Boolean(n));
  if (names.length === 0) return null;
  return (
    `Your subscription is billed through ${names.join(" and ")}. ` +
    "Cancel it there to stop charges."
  );
}

/** The whole confirmation message. A preview that could not be read gets a
 *  conditional store line rather than none: we cannot say the account is not
 *  store-billed, so the person is still told where to cancel. */
export function deletionConfirmMessage(preview: DeletionPreview | null): string {
  const lines: string[] = [];
  if (preview == null) {
    lines.push(
      "If you pay for Sugar Alarm through the App Store or Google Play, " +
        "cancel it there to stop charges.",
    );
  } else {
    const store = storeBillingLine(preview.store_billing);
    if (store) lines.push(store);
    if (preview.cancels_web_subscription) {
      lines.push(
        "Your web subscription will be cancelled immediately. " +
          "Charges already made are not refunded automatically.",
      );
    }
  }
  lines.push(DELETE_ACCOUNT_BODY);
  return lines.join(" ");
}

const GENERIC_FAILURE =
  "We couldn't delete your account just now. Please try again, or " +
  "email support@sugalarm.com to request deletion.";

/** The failure alert. A refused Stripe cancel carries its own server message
 *  (the account was NOT deleted); anything else gets the generic line. */
export function deletionFailureMessage(err: { details?: unknown }): string {
  const details = err.details;
  if (details && typeof details === "object" && "detail" in details) {
    const detail = (details as { detail?: unknown }).detail;
    if (detail && typeof detail === "object") {
      const d = detail as { code?: unknown; message?: unknown };
      if (
        d.code === "subscription_cancel_failed" &&
        typeof d.message === "string" &&
        d.message
      ) {
        return d.message;
      }
    }
  }
  return GENERIC_FAILURE;
}
